'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import TextareaAutosize from 'react-textarea-autosize'
import { motion } from 'motion/react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowUp02Icon, StopIcon, Add01Icon } from '@hugeicons-pro/core-solid-rounded'
import { Film01Icon, File01Icon, Attachment01Icon, Cancel01Icon } from '@hugeicons-pro/core-stroke-rounded'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ImageLightbox } from '@/components/ImageLightbox'
import { SlashCommandPicker } from '@/components/chat/SlashCommandPicker'
import { SlashCommandResultCard } from '@/components/chat/SlashCommandResultCard'
import { MentionPicker } from '@/components/mentions/MentionPicker'
import {
  executeSlashCommand,
  slashCommandRegistry,
  type SlashCommand,
  type SlashCommandContext,
  type SlashCommandResult,
  type SlashCommandUiAction,
} from '@/lib/slash-commands'
import { useCustomCommands } from '@/lib/slash-commands/useCustomCommands'
import {
  findMentionTrigger,
  serializeMentionsForSubmit,
  TOKEN_RE,
  type MentionContext,
  type ResolvedMention,
} from '@/lib/mentions'

export type ChatPromptAttachment = {
  id: string
  name: string
  size: number
  /** Blob URL for image/video row preview + lightbox (parent must revoke on remove). */
  mediaPreviewUrl?: string | null
  /** When set with `mediaPreviewUrl`, row opens the lightbox; otherwise row triggers download. */
  mediaKind?: 'image' | 'video' | null
}

type ActiveMention = { kind: string; id: string; display: string; insertedAt: number }

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export interface ChatPromptBoxProps {
  inputRef: React.RefObject<HTMLTextAreaElement | null>
  fileInputRef: React.RefObject<HTMLInputElement | null>
  composerInputId: string
  input: string
  onInputChange: (value: string) => void
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  placeholder: string
  minRows?: number
  maxRows?: number
  disabled?: boolean
  autoFocus?: boolean
  hasContent: boolean
  isLoading: boolean
  onSend: () => void
  onStop: () => void
  onAttachClick: () => void
  /** Required when `showNewSessionButton` is true (default). */
  onNewSession?: () => void
  /** When false, the new-session control is omitted (e.g. empty chat where it is redundant). */
  showNewSessionButton?: boolean
  /** Passed to the hidden file input (e.g. chat upload allowlist). */
  accept?: string
  /** Disables only the attach button (e.g. while an upload is in flight). */
  attachDisabled?: boolean
  /** Tooltip text shown when attach is disabled. */
  attachDisabledTooltip?: string
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  attachments?: ChatPromptAttachment[]
  onRemoveAttachment?: (id: string) => void
  /** Non-image/video files: trigger a browser download when the row is clicked. */
  onDownloadAttachment?: (id: string) => void
  slashCommandContext?: SlashCommandContext | null
  includeAdminSlashCommands?: boolean
  onSlashLLMRouted?: (prompt: string) => void
  onSlashNavigate?: (path: string) => void
  onSlashUiAction?: (action: SlashCommandUiAction) => void
  promptControls?: React.ReactNode
  footer?: React.ReactNode
  className?: string
}

export function ChatPromptBox({
  inputRef,
  fileInputRef,
  composerInputId,
  input,
  onInputChange,
  onKeyDown,
  placeholder,
  minRows = 2,
  maxRows = 12,
  disabled = false,
  autoFocus = false,
  hasContent,
  isLoading,
  onSend,
  onStop,
  onAttachClick,
  onNewSession,
  showNewSessionButton = true,
  accept,
  attachDisabled = false,
  attachDisabledTooltip,
  onFileChange,
  attachments = [],
  onRemoveAttachment,
  onDownloadAttachment,
  slashCommandContext = null,
  includeAdminSlashCommands = false,
  onSlashLLMRouted,
  onSlashNavigate,
  onSlashUiAction,
  promptControls,
  footer,
  className,
}: ChatPromptBoxProps) {
  const hasAttachments = attachments.length > 0
  const customCommandsVersion = useCustomCommands(slashCommandContext?.workspaceId)
  const [lightboxAttachmentId, setLightboxAttachmentId] = useState<string | null>(null)
  const [slashIndex, setSlashIndex] = useState(0)
  const [slashDismissedInput, setSlashDismissedInput] = useState<string | null>(null)
  const [slashResult, setSlashResult] = useState<Exclude<SlashCommandResult, { kind: 'llm-routed' }> | null>(null)
  // Mention picker state
  const [mentionQuery, setMentionQuery] = useState<string | null>(null)
  const [mentionTriggerStart, setMentionTriggerStart] = useState(-1)
  const [mentionIndex, setMentionIndex] = useState(0)
  const [mentionDismissedAt, setMentionDismissedAt] = useState<number>(-1)
  const [mentions, setMentions] = useState<ActiveMention[]>([])
  // The MentionPicker reports its current candidate list here so the
  // textarea's keyboard handler can confirm the selection on Tab/Enter.
  const mentionCandidatesRef = useRef<ResolvedMention[]>([])
  const overlayRef = useRef<HTMLDivElement>(null)
  const onSendRef = useRef(onSend)
  onSendRef.current = onSend
  const lightboxAttachment =
    lightboxAttachmentId != null
      ? attachments.find(a => a.id === lightboxAttachmentId)
      : undefined
  const mentionContext: MentionContext | null = slashCommandContext
    ? { workspaceId: slashCommandContext.workspaceId, userId: slashCommandContext.userId, supabase: slashCommandContext.supabase }
    : null
  const mentionOpen =
    Boolean(mentionContext) &&
    mentionQuery !== null &&
    mentionTriggerStart !== mentionDismissedAt &&
    !disabled

  const isSlashInput = input.startsWith('/') && !input.includes('\n')
  const slashQuery = isSlashInput ? input.slice(1).split(/\s+/)[0] ?? '' : ''
  const slashCommands = useMemo(() => {
    void customCommandsVersion
    return slashCommandRegistry.search(slashQuery, { includeAdmin: includeAdminSlashCommands })
  }, [customCommandsVersion, includeAdminSlashCommands, slashQuery])
  const slashOpen = Boolean(slashCommandContext) && isSlashInput && slashDismissedInput !== input && slashCommands.length > 0 && !disabled

  useEffect(() => {
    if (lightboxAttachmentId && !attachments.some(a => a.id === lightboxAttachmentId)) {
      setLightboxAttachmentId(null)
    }
  }, [attachments, lightboxAttachmentId])

  useEffect(() => {
    setSlashIndex(0)
    if (!isSlashInput) setSlashDismissedInput(null)
  }, [isSlashInput, slashQuery])

  useEffect(() => {
    const found: ActiveMention[] = []
    const replaced = input.replace(new RegExp(TOKEN_RE.source, 'gi'), (_match, kind, id, display) => {
      found.push({ kind, id, display, insertedAt: Date.now() })
      return `@${display}`
    })
    if (replaced !== input) {
      onInputChange(replaced)
      setMentions(prev => [...prev, ...found])
    }
  }, [input, onInputChange])

  useEffect(() => {
    if (input.includes('[[@')) return
    setMentions(prev => prev.filter(m => input.includes(`@${m.display}`)))
  }, [input])

  const insertMention = useCallback((mention: ResolvedMention) => {
    const cursor = inputRef.current?.selectionStart ?? input.length
    const displayForm = `@${mention.display}`
    const before = input.slice(0, mentionTriggerStart)
    const after = input.slice(cursor)
    const next = before + displayForm + after
    onInputChange(next)
    setMentions(prev => [...prev, { kind: mention.kind, id: mention.id, display: mention.display, insertedAt: Date.now() }])
    setMentionQuery(null)
    setMentionTriggerStart(-1)
    requestAnimationFrame(() => {
      if (inputRef.current) {
        const pos = before.length + displayForm.length
        inputRef.current.selectionStart = pos
        inputRef.current.selectionEnd = pos
        inputRef.current.focus()
      }
    })
  }, [input, mentionTriggerStart, onInputChange, inputRef])

  // Render the input as segments for the chip overlay
  const overlaySegments = useMemo(() => {
    if (mentions.length === 0) return [{ type: 'text' as const, value: input }]

    const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const sorted = [...mentions].sort((a, b) => b.display.length - a.display.length)
    const pattern = new RegExp(
      sorted.map(m => `@${escapeRe(m.display)}`).join('|'),
      'g'
    )

    const segs: Array<{ type: 'text'; value: string } | { type: 'chip'; display: string }> = []
    let last = 0
    let match: RegExpExecArray | null
    while ((match = pattern.exec(input)) !== null) {
      if (match.index > last) segs.push({ type: 'text', value: input.slice(last, match.index) })
      segs.push({ type: 'chip', display: match[0].slice(1) })
      last = match.index + match[0].length
    }
    if (last < input.length) segs.push({ type: 'text', value: input.slice(last) })
    return segs
  }, [input, mentions])

  const hasChips = overlaySegments.some(s => s.type === 'chip')

  const runSlashCommand = async (commandInput: string) => {
    if (!slashCommandContext) return
    const result = await executeSlashCommand(commandInput, slashCommandContext)

    if (result.kind === 'llm-routed') {
      setSlashResult(null)
      onInputChange('')
      // Use displayMessage (with mention chip token) when available
      onSlashLLMRouted?.(result.displayMessage ?? result.prompt)
      return
    }

    if (result.kind === 'ui-action') {
      onInputChange('')
      onSlashUiAction?.(result.action)
      // Show a transient confirmation card only if the action carries a message.
      setSlashResult(result.message ? { kind: 'success', message: result.message } : null)
      return
    }

    setSlashResult(result)
    onInputChange('')

    if (result.kind === 'navigate') {
      onSlashNavigate?.(result.path)
    }
  }

  const selectSlashCommand = (command: SlashCommand) => {
    // If the command's first arg expects a campaign reference, auto-prefix
    // the next param with "@" so the mention picker opens immediately and
    // the user can keep typing the campaign name without an extra keystroke.
    const firstArgIsCampaign = command.args[0]?.type === 'campaign'
    const trailing = command.args.length === 0
      ? ''
      : firstArgIsCampaign
        ? ' @'
        : ' '
    const nextInput = `/${command.name}${trailing}`
    onInputChange(nextInput)
    setSlashDismissedInput(command.args.length > 0 ? nextInput : null)

    if (command.args.length === 0) {
      void runSlashCommand(nextInput)
      return
    }

    // Move the textarea cursor to the end so subsequent typing lands after
    // the auto-injected param prefix. Programmatic setState doesn't fire the
    // textarea's onChange, so we also seed the mention picker state directly
    // when we injected an `@`.
    requestAnimationFrame(() => {
      const el = inputRef.current
      if (el) {
        el.focus()
        el.selectionStart = nextInput.length
        el.selectionEnd = nextInput.length
      }
    })
    if (firstArgIsCampaign) {
      // The trailing `@` sits at index (nextInput.length - 1).
      setMentionQuery('')
      setMentionTriggerStart(nextInput.length - 1)
      setMentionIndex(0)
      setMentionDismissedAt(-1)
    }
  }

  const serializeForSubmit = useCallback((text: string): string => {
    return serializeMentionsForSubmit(text, mentions)
  }, [mentions])

  const submitInput = useCallback(() => {
    const serialized = serializeForSubmit(input)
    if (serialized !== input) {
      onInputChange(serialized)
      requestAnimationFrame(() => {
        onSendRef.current()
        setMentions([])
      })
      return
    }
    onSendRef.current()
    setMentions([])
  }, [input, onInputChange, serializeForSubmit])

  const removeMentionAt = useCallback((start: number, end: number, display: string) => {
    const next = input.slice(0, start) + input.slice(end)
    onInputChange(next)
    setMentions(prev => {
      const idx = prev.findIndex(m => m.display === display)
      if (idx === -1) return prev
      return [...prev.slice(0, idx), ...prev.slice(idx + 1)]
    })
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.selectionStart = start
        inputRef.current.selectionEnd = start
      }
    })
  }, [input, inputRef, onInputChange])

  const findChipContainingCursor = useCallback((cursor: number) => {
    for (const mention of [...mentions].sort((a, b) => b.display.length - a.display.length)) {
      const displayForm = `@${mention.display}`
      let start = input.indexOf(displayForm)
      while (start !== -1) {
        const end = start + displayForm.length
        if (cursor > start && cursor < end) {
          return { start, end, display: mention.display }
        }
        start = input.indexOf(displayForm, end)
      }
    }
    return null
  }, [input, mentions])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Mention picker takes priority over slash picker
    if (mentionOpen) {
      const candidateCount = mentionCandidatesRef.current.length
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (candidateCount > 0) setMentionIndex(i => (i + 1) % candidateCount)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (candidateCount > 0) setMentionIndex(i => (i - 1 + candidateCount) % candidateCount)
        return
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
        const candidates = mentionCandidatesRef.current
        if (candidates.length === 0) {
          // Nothing to confirm; let the keystroke fall through (Enter sends, Tab moves focus).
          return
        }
        e.preventDefault()
        const clamped = Math.min(Math.max(mentionIndex, 0), candidates.length - 1)
        insertMention(candidates[clamped])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setMentionDismissedAt(mentionTriggerStart)
        return
      }
    }

    if (slashOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSlashIndex(i => (i + 1) % slashCommands.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSlashIndex(i => (i - 1 + slashCommands.length) % slashCommands.length)
        return
      }
      if (e.key === 'Tab') {
        e.preventDefault()
        selectSlashCommand(slashCommands[slashIndex])
        return
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        const exactCommand = slashCommandRegistry.get(slashQuery)
        if (exactCommand && input.trim() === `/${slashQuery}`) {
          void runSlashCommand(input)
        } else {
          selectSlashCommand(slashCommands[slashIndex])
        }
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setSlashDismissedInput(input)
        return
      }
    }

    if (e.key === 'Backspace' && !mentionOpen) {
      const cursor = inputRef.current?.selectionStart ?? -1
      if (cursor > 0 && inputRef.current?.selectionStart === inputRef.current?.selectionEnd) {
        const upToCursor = input.slice(0, cursor)
        for (const mention of mentions) {
          const displayForm = `@${mention.display}`
          if (upToCursor.endsWith(displayForm)) {
            e.preventDefault()
            const start = cursor - displayForm.length
            removeMentionAt(start, cursor, mention.display)
            return
          }
        }
      }
    }

    const keyDeletesChip =
      !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Shift', 'Meta', 'Control', 'Alt', 'Escape', 'Tab'].includes(e.key) &&
      !e.metaKey &&
      !e.ctrlKey &&
      !e.altKey &&
      inputRef.current?.selectionStart === inputRef.current?.selectionEnd

    if (keyDeletesChip) {
      const cursor = inputRef.current?.selectionStart ?? -1
      const chip = findChipContainingCursor(cursor)
      if (chip) {
        e.preventDefault()
        removeMentionAt(chip.start, chip.end, chip.display)
        return
      }
    }

    if (isSlashInput && e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void runSlashCommand(input)
      return
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submitInput()
      return
    }

    onKeyDown(e)
  }

  const handleSendClick = () => {
    if (isSlashInput && slashCommandContext) {
      void runSlashCommand(input)
      return
    }

    submitInput()
  }

  const sendButton = isLoading ? (
    <button
      type="button"
      onClick={onStop}
      aria-label="Stop"
      className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-accent-600 text-white hover:bg-accent-700 transition-colors"
    >
      <HugeiconsIcon icon={StopIcon} className="size-3.5" />
    </button>
  ) : (
    <button
      type="button"
      onClick={handleSendClick}
      disabled={!hasContent}
      aria-label="Send message"
      className={cn(
        'flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors',
        hasContent
          ? 'cursor-pointer bg-accent-600 text-white hover:bg-accent-700'
          : 'cursor-not-allowed bg-accent-600 text-white opacity-50'
      )}
    >
      <HugeiconsIcon icon={ArrowUp02Icon} className="size-4" />
    </button>
  )

  return (
    <>
    {slashResult ? <SlashCommandResultCard result={slashResult} /> : null}
    <div
      className={cn(
        'overflow-visible relative isolate flex max-h-[min(45vh,280px)] flex-col bg-atrium-inner rounded-xl border border-atrium-border/60 focus-within:border-atrium-border ring-1 ring-atrium-black/5 dark:ring-black',
        className
      )}
    >
      {mentionOpen && mentionContext ? (
        <MentionPicker
          query={mentionQuery ?? ''}
          open={mentionOpen}
          selectedIndex={mentionIndex}
          onSelectedIndexChange={setMentionIndex}
          onSelect={insertMention}
          onCandidatesChange={(c) => { mentionCandidatesRef.current = c }}
          mentionContext={mentionContext}
        />
      ) : slashOpen ? (
        <SlashCommandPicker
          input={input}
          open={slashOpen}
          selectedIndex={slashIndex}
          onSelectedIndexChange={setSlashIndex}
          onSelect={selectSlashCommand}
          includeAdmin={includeAdminSlashCommands}
          registryVersion={customCommandsVersion}
        />
      ) : null}

      <input
        ref={fileInputRef as React.Ref<HTMLInputElement>}
        type="file"
        multiple
        accept={accept}
        className="hidden"
        tabIndex={-1}
        onChange={onFileChange}
      />

      <div
        className={cn(
          'grid shrink-0 transition-[grid-template-rows] duration-300 ease-out',
          hasAttachments ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
        aria-hidden={!hasAttachments}
      >
        <div className="min-h-0 overflow-hidden">
          <motion.div
            initial={false}
            animate={{
              opacity: hasAttachments ? 1 : 0,
              y: hasAttachments ? 0 : 10,
            }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            className="border-b border-atrium-border/70 bg-atrium-background/40"
          >
            <ul
              className="max-h-28 overflow-y-auto px-2 py-2 space-y-1"
              aria-label="Attached files"
            >
              {attachments.map(a => {
                const isLightboxMedia =
                  Boolean(a.mediaPreviewUrl) &&
                  (a.mediaKind === 'image' || a.mediaKind === 'video')
                const canDownload = Boolean(onDownloadAttachment) && !isLightboxMedia
                const canInteract = isLightboxMedia || canDownload
                const open = () => {
                  if (!canInteract) return
                  if (isLightboxMedia && a.mediaPreviewUrl) {
                    setLightboxAttachmentId(a.id)
                  } else {
                    onDownloadAttachment?.(a.id)
                  }
                }
                return (
                  <li key={a.id} className="flex min-w-0 items-center gap-1 rounded-lg py-0.5">
                    <button
                      type="button"
                      disabled={!canInteract}
                      onClick={open}
                      className={cn(
                        'flex min-w-0 flex-1 items-center gap-2 rounded-lg px-1.5 py-1 text-left',
                        canInteract &&
                          'cursor-pointer hover:bg-atrium-content/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-600 focus-visible:ring-offset-1',
                        !canInteract && 'cursor-default'
                      )}
                      aria-label={
                        canInteract
                          ? isLightboxMedia
                            ? `Open preview: ${a.name}`
                            : `Download ${a.name}`
                          : undefined
                      }
                    >
                      {a.mediaPreviewUrl && a.mediaKind === 'image' ? (
                        <span className="relative size-9 shrink-0 overflow-hidden rounded-md border border-atrium-border/60 bg-atrium-background">
                          <img
                            src={a.mediaPreviewUrl}
                            alt=""
                            className="size-full object-cover"
                          />
                        </span>
                      ) : a.mediaKind === 'video' ? (
                        <span className="relative size-9 shrink-0 overflow-hidden rounded-md border border-atrium-border/60 bg-atrium-background flex items-center justify-center">
                          <HugeiconsIcon icon={Film01Icon} className="size-4 text-atrium-black/50" aria-hidden />
                        </span>
                      ) : (
                        <HugeiconsIcon icon={File01Icon} className="size-3.5 shrink-0 text-atrium-black/45" aria-hidden />
                      )}
                      <span className="min-w-0 flex-1 truncate text-xs text-atrium-black" title={a.name}>
                        {a.name}
                      </span>
                      <span className="shrink-0 text-[10px] tabular-nums text-[#939294]">
                        {formatFileSize(a.size)}
                      </span>
                    </button>
                    {onRemoveAttachment ? (
                      <button
                        type="button"
                        onClick={() => onRemoveAttachment(a.id)}
                        disabled={isLoading}
                        className="shrink-0 rounded-md p-1 text-[#939294] transition-colors hover:bg-atrium-content hover:text-atrium-black disabled:pointer-events-none disabled:opacity-40"
                        aria-label={`Remove ${a.name}`}
                      >
                        <HugeiconsIcon icon={Cancel01Icon} className="size-3.5" />
                      </button>
                    ) : null}
                  </li>
                )
              })}
            </ul>
          </motion.div>
        </div>
      </div>

      <div className="relative z-0 min-h-0 w-full max-h-[min(40vh,200px)] overflow-hidden">
        <TextareaAutosize
          ref={inputRef as React.ComponentProps<typeof TextareaAutosize>['ref']}
          id={composerInputId}
          data-chat-input
          autoFocus={autoFocus}
          value={input}
          onChange={e => {
            setSlashResult(null)
            const val = e.target.value
            onInputChange(val)
            // Detect @-mention trigger
            const cursor = e.target.selectionStart ?? val.length
            const trigger = findMentionTrigger(val, cursor)
            if (trigger) {
              setMentionQuery(trigger.query)
              setMentionTriggerStart(trigger.start)
              setMentionIndex(0)
            } else {
              setMentionQuery(null)
              setMentionTriggerStart(-1)
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={hasChips || input ? undefined : ''}
          minRows={minRows}
          maxRows={maxRows}
          disabled={disabled}
          className={cn(
            'relative z-0 w-full min-h-0 resize-none overflow-y-auto bg-transparent p-5 text-sm leading-relaxed focus:outline-none max-h-[min(40vh,200px)]',
            hasChips ? 'caret-atrium-black text-transparent' : 'text-atrium-black'
          )}
        />
        {!hasChips && !input ? (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 z-10 overflow-hidden p-5 text-sm leading-relaxed text-[#939294] whitespace-pre-wrap break-words"
          >
            {placeholder}
          </div>
        ) : null}
        {/* Chip overlay mirrors the textarea's @display text exactly. */}
        {hasChips && (
          <div
            ref={overlayRef}
            aria-hidden
            className="pointer-events-none absolute inset-0 z-10 overflow-hidden p-5 text-sm leading-relaxed text-atrium-black whitespace-pre-wrap break-words"
          >
            {overlaySegments.map((seg, i) => {
              if (seg.type === 'text') return <span key={i}>{seg.value}</span>
              return (
                <span key={i} className="align-baseline pointer-events-auto cursor-default text-accent-600">
                  @{seg.display}
                </span>
              )
            })}
          </div>
        )}
      </div>
      <div
        className="relative z-[1] flex shrink-0 cursor-text items-center gap-2 px-2 py-1.5 pointer-events-auto"
        onPointerDown={e => {
          if ((e.target as HTMLElement).closest('button')) return
          inputRef.current?.focus()
        }}
      >
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              type="button"
              onClick={attachDisabled ? undefined : onAttachClick}
              variant="ghost"
              aria-label="Attach file"
              aria-disabled={isLoading || attachDisabled}
              className={cn(
                'size-8 shrink-0 hover:bg-atrium-content text-atrium-black/60 hover:text-atrium-black cursor-pointer',
                (isLoading || attachDisabled) && 'opacity-40 cursor-default'
              )}
            >
              <HugeiconsIcon icon={Attachment01Icon} className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            {attachDisabled && attachDisabledTooltip ? attachDisabledTooltip : 'Attach file'}
          </TooltipContent>
        </Tooltip>
        {/* Stretch spacer — pushes promptControls (Ask/Agent + model picker)
            and Send button to the right edge of the composer footer. */}
        <label htmlFor={composerInputId} className="min-h-8 min-w-0 flex-1 cursor-text" />
        {promptControls}
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{sendButton}</TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            {isLoading ? 'Stop' : 'Send message'}
          </TooltipContent>
        </Tooltip>
      </div>

      {lightboxAttachment?.mediaPreviewUrl &&
      (lightboxAttachment.mediaKind === 'image' || lightboxAttachment.mediaKind === 'video') ? (
        <ImageLightbox
          imageUrl={lightboxAttachment.mediaPreviewUrl}
          mediaType={lightboxAttachment.mediaKind === 'video' ? 'video' : 'image'}
          onClose={() => setLightboxAttachmentId(null)}
        />
      ) : null}
    </div>
    {footer ?? (
      <div className="mt-1.5 flex w-full max-w-full items-center justify-between gap-2 px-2">
        {showNewSessionButton && onNewSession ? (
          <Button
            type="button"
            variant="ghost"
            onClick={() => void onNewSession()}
            disabled={isLoading}
            aria-label="New session"
            className="h-6 shrink-0 gap-1 px-1.5 text-[11px] font-medium text-atrium-black/50 hover:bg-atrium-content hover:text-atrium-black/80 disabled:pointer-events-none disabled:opacity-40"
          >
            <HugeiconsIcon icon={Add01Icon} className="size-3" />
            New session
          </Button>
        ) : (
          <span />
        )}
        <p className="max-w-md text-right text-[10px] leading-snug text-atrium-muted">
          Atrium is AI and can make mistakes. Check all info.
        </p>
      </div>
    )}
    </>
  )
}
